import axios from "axios";
import { ApiURL } from "../../setting/GlobleVariable";

export const DOOR_SESSION_KEY = "instapass_door_session";

/** Headers for an organizer (signed-in user) or door staff (door code session). */
export function authHeaders(auth) {
  if (auth?.doorToken) return { "door-token": auth.doorToken };
  return { token: localStorage.getItem("instapass_user_token") || "" };
}

function errorMessage(error, fallback) {
  if (error?.code === "ECONNABORTED") return "Network is slow. Use Backup check-in to find the guest by name.";
  if (!error?.response && error?.request) return "No connection. Use Backup check-in, or move to better signal.";
  return error?.response?.data?.message || error?.message || fallback;
}

async function call(promise, fallback) {
  try {
    const { data } = await promise;
    return data;
  } catch (error) {
    const err = new Error(errorMessage(error, fallback));
    err.status = error?.response?.status;
    err.network = !error?.response;
    throw err;
  }
}

export const ticketApi = {
  myTickets: () =>
    call(axios.get(`${ApiURL}/tickets/mine`, { headers: authHeaders() }), "Couldn't load your tickets"),

  myEvents: () =>
    call(axios.get(`${ApiURL}/event/my-events`, { headers: authHeaders() }), "Couldn't load your events"),

  doorLogin: (code) =>
    call(axios.post(`${ApiURL}/tickets/door/login`, { code }), "That door code isn't valid"),

  // 3-second timeout: on a slow network the door falls back to name search (spec)
  scan: (auth, payload, eventId) =>
    call(
      axios.post(`${ApiURL}/tickets/scan`, { payload, eventId }, { headers: authHeaders(auth), timeout: 3000 }),
      "Scan failed"
    ),

  manualCheckIn: (auth, ticketId) =>
    call(
      axios.post(`${ApiURL}/tickets/${ticketId}/checkin`, {}, { headers: authHeaders(auth) }),
      "Check-in failed"
    ),

  stats: (auth, eventId) =>
    call(axios.get(`${ApiURL}/tickets/event/${eventId}/stats`, { headers: authHeaders(auth) }), "Couldn't load counts"),

  attendees: (auth, eventId, { q = "", status = "" } = {}) =>
    call(
      axios.get(`${ApiURL}/tickets/event/${eventId}/attendees`, {
        headers: authHeaders(auth),
        params: { q: q || undefined, status: status || undefined },
      }),
      "Couldn't load attendees"
    ),

  exportCsv: async (eventId) => {
    const res = await axios.get(`${ApiURL}/tickets/event/${eventId}/attendees.csv`, {
      headers: authHeaders(),
      responseType: "blob",
    });
    return res.data;
  },

  resend: (ticketId) =>
    call(axios.post(`${ApiURL}/tickets/${ticketId}/resend`, {}, { headers: authHeaders() }), "Couldn't resend"),

  voidTicket: (ticketId, reason) =>
    call(axios.post(`${ApiURL}/tickets/${ticketId}/void`, { reason }, { headers: authHeaders() }), "Couldn't void ticket"),

  refund: (ticketIds, reason) =>
    call(axios.post(`${ApiURL}/tickets/refund`, { ticketIds, reason }, { headers: authHeaders() }), "Couldn't refund ticket"),

  doorCodes: (eventId) =>
    call(axios.get(`${ApiURL}/tickets/event/${eventId}/door-codes`, { headers: authHeaders() }), "Couldn't load door codes"),

  createDoorCode: (eventId, label) =>
    call(
      axios.post(`${ApiURL}/tickets/event/${eventId}/door-codes`, { label }, { headers: authHeaders() }),
      "Couldn't create door code"
    ),

  revokeDoorCode: (id) =>
    call(axios.delete(`${ApiURL}/tickets/door-codes/${id}`, { headers: authHeaders() }), "Couldn't turn off door code"),
};
